import { Link, useLocation } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import { Award, ChevronRight, Zap, Shield, Thermometer, Eye, Plug, Sun, Car, AlertTriangle, FileCheck, Leaf, Battery, Flame, Droplets, ClipboardCheck } from "lucide-react";

const ELECTRICAL_CERTS = [
  { id: "minor-works", name: "Minor Works", icon: Zap, available: true },
  { id: "3-circuit-minor", name: "3 Circuit Minor Works", icon: Zap, available: true },
  { id: "eic", name: "Electrical Installation Certificate", icon: FileCheck, available: true, route: "/certificates/elec/eic" },
  { id: "domestic-eic", name: "Domestic EIC", icon: FileCheck, available: true },
  { id: "eicr", name: "EICR", icon: Shield, available: true, route: "/certificates/elec/eicr" },
  { id: "danger-notice", name: "Electrical Danger Notice", icon: AlertTriangle, available: true, route: "/certificates/elec/danger-notice" },
  { id: "visual-inspection", name: "Visual Inspection", icon: Eye, available: true, route: "/certificates/elec/visual-inspection" },
  { id: "earthing-bonding", name: "Earthing & Bonding", icon: Plug, available: true, route: "/certificates/elec/earthing-bonding" },
  { id: "electrical-isolation", name: "Electrical Isolation", icon: Plug, available: true, route: "/certificates/elec/electrical-isolation" },
  { id: "ev-risk-assessment", name: "EV Risk Assessment", icon: Car, available: true, route: "/certificates/elec/ev-risk-assessment" },
  { id: "electrical-risk", name: "Electrical Risk Assessment", icon: AlertTriangle, available: true, route: "/certificates/elec/electrical-risk" },
  { id: "ventilation", name: "Ventilation Certificate", icon: Thermometer, available: true, route: "/certificates/elec/ventilation" },
  { id: "solar-pv", name: "Solar PV", icon: Sun, available: true, route: "/certificates/elec/solar-pv" },
  { id: "ev-charger", name: "EV Charger Point", icon: Car, available: true, route: "/certificates/elec/ev-charger" },
  { id: "landlords-interim", name: "Landlords Interim Check", icon: Shield, available: true, route: "/certificates/elec/landlords-interim" },
];

const MCS_DOCUMENTS = [
  { id: "mcs-solar-pv-install", name: "Solar PV Installation Checklist", icon: Sun, available: true },
  { id: "mcs-solar-pv-commissioning", name: "Solar PV Commissioning Checklist", icon: ClipboardCheck, available: true },
  { id: "mcs-solar-pv-array-test", name: "Solar PV Array Test Form", icon: Sun, available: true },
  { id: "mcs-solar-pv-handover", name: "Solar PV Handover Document", icon: FileCheck, available: true },
  { id: "mcs-solar-pv-battery-handover", name: "Solar PV & Battery Handover", icon: Battery, available: true },
  { id: "mcs-battery-handover", name: "Battery Handover Document", icon: Battery, available: true },
  { id: "mcs-battery-commissioning", name: "Battery Commissioning Checklist", icon: Battery, available: true },
  { id: "mcs-battery-testing", name: "Battery Testing Checklist", icon: Battery, available: true },
  { id: "mcs-heat-pump-commissioning", name: "Heat Pump Commissioning Checklist", icon: Flame, available: true },
  { id: "mcs-heat-pump-handover", name: "Heat Pump Handover Certificate", icon: Flame, available: true },
  { id: "mcs-cylinder-commissioning", name: "Cylinder Commissioning Checklist", icon: Droplets, available: true },
  { id: "mcs-dno-ev-hp", name: "DNO EV & HP Application", icon: ClipboardCheck, available: true },
  { id: "mcs-ashp-benchmark", name: "ASHP Commissioning Benchmark", icon: Flame, available: true },
  { id: "mcs-gshp-benchmark", name: "GSHP Commissioning Benchmark", icon: Flame, available: true },
  { id: "mcs-hybrid-hp-benchmark", name: "Hybrid HP (Gas+ASHP) Benchmark", icon: Flame, available: true },
];

function CertGrid({ items, testId, accentColor = "#D4A537", linkPrefix = "/certificates/" }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={testId}>
      {items.map((cert) => {
        const Icon = cert.icon;
        const href = cert.route || (cert.id.startsWith("mcs-") ? `/certificates/mcs/${cert.id.replace("mcs-", "")}` : `${linkPrefix}${cert.id}`);
        if (cert.available) {
          return (
            <Link
              key={cert.id}
              to={href}
              data-testid={`cert-type-${cert.id}`}
              className="brutalist-card p-4 flex items-center gap-4 transition-colors group"
              style={{ borderColor: 'var(--border-main)' }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = accentColor}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-main)'}
            >
              <div className="w-10 h-10 flex items-center justify-center border-2 flex-shrink-0 group-hover:scale-105 transition-transform"
                style={{ backgroundColor: accentColor, borderColor: 'var(--border-main)' }}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <span className="flex-1 text-sm font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{cert.name}</span>
              <ChevronRight className="w-5 h-5" style={{ color: accentColor }} />
            </Link>
          );
        }
        return (
          <div key={cert.id} data-testid={`cert-type-${cert.id}`}
            className="brutalist-card p-4 flex items-center gap-4 opacity-50 cursor-not-allowed">
            <div className="w-10 h-10 bg-[#B8B9BD] flex items-center justify-center border-2 flex-shrink-0" style={{ borderColor: 'var(--border-light)' }}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            <span className="flex-1 text-sm font-semibold" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-secondary)' }}>{cert.name}</span>
            <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Coming Soon</span>
          </div>
        );
      })}
    </div>
  );
}

export default function CertificateSelectPage() {
  const location = useLocation();
  const path = location.pathname;
  const section = path === "/new/electrical" ? "electrical" : path === "/new/mcs" ? "mcs" : null;
  const showElectrical = !section || section === "electrical";
  const showMCS = !section || section === "mcs";

  const title = section === "electrical" ? "Electrical Certificates" : section === "mcs" ? "MCS Compliance Documents" : "Certificates & Documents";
  const TitleIcon = section === "mcs" ? Leaf : Award;
  const titleColor = section === "mcs" ? "#22C55E" : "#00A3E0";

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)' }} data-testid="certificate-select-page">
      <AppHeader />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-2">
          <TitleIcon className="w-7 h-7" style={{ color: titleColor }} />
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {title}
          </h1>
        </div>
        <p className="text-sm mb-10" style={{ color: 'var(--text-secondary)' }}>Select the type of {section === "mcs" ? "MCS document" : section === "electrical" ? "electrical certificate" : "certificate or document"} you need to create</p>

        {/* Electrical Certificates */}
        {showElectrical && (
          <div className="mb-10" data-testid="section-electrical">
            {!section && (
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-1 h-6 bg-[#00A3E0]" />
                <h2 className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Electrical Certificates
                </h2>
              </div>
            )}
            <CertGrid items={ELECTRICAL_CERTS} testId="electrical-cert-list" accentColor="#00A3E0" />
          </div>
        )}

        {/* MCS Compliance */}
        {showMCS && (
          <div data-testid="section-mcs">
            {!section && (
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-1 h-6 bg-[#22C55E]" />
                <div className="flex items-center gap-2">
                  <Leaf className="w-5 h-5 text-[#22C55E]" />
                  <h2 className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    MCS Compliance
                  </h2>
                </div>
              </div>
            )}
            <CertGrid items={MCS_DOCUMENTS} testId="mcs-cert-list" accentColor="#22C55E" />
          </div>
        )}
      </div>
    </div>
  );
}
